import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { TranslateDirective, TranslateService } from '@ngx-translate/core';

import { DetailedQuote } from '../../../../domain/public-api';
import { NumberPipe } from '../../../../pipes/number.pipe';
import { OverlayToggleDirective } from '../../../../components/overlay/overlay-toggle.directive';
import { OverlayComponent } from '../../../../components/overlay/overlay.component';
import { OverlayHeaderComponent } from '../../../../components/overlay/overlay-header.component';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { VolumePace } from '../domain';

@Component({
  selector: 'td-wb-quote-volume-contextual-help',
  templateUrl: 'quote-volume-contextual-help.component.html',
  styleUrls: ['quote-volume-contextual-help.component.scss'],
  imports: [
    NumberPipe,
    OverlayToggleDirective,
    OverlayComponent,
    OverlayHeaderComponent,
    TranslatePipe,
    NgTemplateOutlet,
    TranslateDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuoteVolumeContextualHelpComponent implements OnChanges {

  /**
   * Quote object
   */
  @Input() quote: DetailedQuote;

  /**
   * Overlay link text
   */
  link: string;

  /**
   * Overlay body text
   */
  body: string;

  constructor(
    private translateService: TranslateService,
    private numberPipe: NumberPipe
  ) { }

  /**
   * Handle quote changes
   *
   * @param
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change: SimpleChange =  changes['quote'];
    if (change === undefined || change.currentValue === undefined) {
      return;
    }
    const currentValue = change.currentValue;
    if (currentValue.symbol !== undefined ||
        currentValue.volume !== undefined ||
        currentValue.volumePace !== undefined) {
      this.refresh();
    }
  }

  /**
   * Return true if session is after open
   *
   * @returns
   */
  isSessionAfterOpen(): boolean {
    return this.quote.sessionIsAfterOpen === true;
  }

  /**
   * Return true if session is before open
   *
   * @returns
   */
  isSessionBeforeOpen(): boolean {
    return this.quote.sessionIsBeforeOpen === true;
  }

  /**
   * Set link and body used for overlay
   */
  private refresh(): void {
    const values: { volume: string; symbol: string; volumeTenDays: string } = {
      volume: this.numberPipe.transform(Math.round(this.quote.volume), { format: 'n0' }),
      symbol: this.quote.symbol,
      volumeTenDays: this.numberPipe.transform(Math.round(this.quote.volumeTenDays), { format: 'n0' })
    };
    switch (this.quote.volumePace) {
      case VolumePace.LIGHT:
        this.setLinkBody('tdWb.markets.quote.volumecontextualhelp.LIGHT',
          'tdWb.markets.quote.volumecontextualhelp.IS_LIGHTER_THAN', values);
        break;
      case VolumePace.BELOWAVG:
        this.setLinkBody('tdWb.markets.quote.volumecontextualhelp.BELOW_AVERAGE',
          'tdWb.markets.quote.volumecontextualhelp.IS_BELOW', values);
        break;
      case VolumePace.AVG:
        this.setLinkBody('tdWb.markets.quote.volumecontextualhelp.AVERAGE',
          'tdWb.markets.quote.volumecontextualhelp.IS_EQUAL_TO', values);
        break;
      case VolumePace.ABOVEAVG:
        this.setLinkBody('tdWb.markets.quote.volumecontextualhelp.ABOVE_AVERAGE',
          'tdWb.markets.quote.volumecontextualhelp.IS_ABOVE', values);
        break;
      case VolumePace.HEAVY:
        this.setLinkBody('tdWb.markets.quote.volumecontextualhelp.HEAVY',
          'tdWb.markets.quote.volumecontextualhelp.IS_MUCH_GREATER_THAN', values);
        break;
      default:
        this.setLinkBody('tdWb.markets.quote.NOT_AVAILABLE',
          'tdWb.markets.quote.NOT_AVAILABLE');
        break;
    }
  }

  /**
   * Translate link and body text
   */
  private setLinkBody(link: string, body: string, values: { volume: string; symbol: string; volumeTenDays: string } = undefined): void {
    this.link = this.translateService.instant(link);
    if (this.quote.sessionIsActive) {
      if (values !== undefined) {
        this.body = this.translateService.instant(body, values);
      } else {
        this.body = this.translateService.instant(body);
      }
    }
  }
}
